// Deterministic synthetic manufacturing/QC dataset.
// ~4,500 run records, Jan 2025 - Aug 2026. All values fictional.
function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rand = mulberry32(20250817);
function gauss() {
  let u = 0,
    v = 0;
  while (u === 0) u = rand();
  while (v === 0) v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
const r2 = (x) => Number(x.toFixed(2));
const pick = (a) => a[Math.floor(rand() * a.length)];
function weighted(items) {
  const total = items.reduce((s, i) => s + i[1], 0);
  let r = rand() * total;
  for (const [v, w] of items) {
    r -= w;
    if (r <= 0) return v;
  }
  return items[items.length - 1][0];
}

export const PRODUCTS = ["HelixIEF-200", "HelixIEF-400", "QuantSDS", "RapidSDS-X", "NovaFlow-FX"];
export const INSTRUMENTS = Array.from({ length: 10 }, (_, i) => `INS-${String(i + 1).padStart(2, "0")}`);
export const REAGENT_LOTS = Array.from({ length: 12 }, (_, i) => `RGT-${101 + i}`);
export const COMPONENT_LOTS = Array.from({ length: 15 }, (_, i) => `CMP-${201 + i}`);
export const QC_STAGES = ["In-Process", "Release", "Stability", "Raw Material"];

export const FAILURE_MODES = [
  ["High Migration Time", 14],
  ["Low Resolution", 10],
  ["Signal Noise Excursion", 12],
  ["Pressure Setpoint Drift", 8],
  ["Missing Reference Peak", 6],
  ["Microbubble-Induced Signal Artifact", 7],
  ["Irregular Separation Profile", 7],
  ["Flow Restriction", 6],
  ["Current Stability Excursion", 5],
  ["Temperature Excursion", 5],
  ["Calibration Drift", 4],
  ["Signal Height Below Threshold", 5],
  ["Abnormal Baseline", 4],
  ["Reference Marker Shift", 3],
  ["Reagent Performance Excursion", 3],
  ["Component-Lot Correlation", 1],
];
export const FAILURE_MODE_NAMES = FAILURE_MODES.map((f) => f[0]);

// Fictional product-aware applicability layer. This is a demonstration data-architecture
// construct only: it is not a real product specification, control strategy or failure library.
export const syntheticProductApplicability = Object.freeze({
  "HelixIEF-200": {
    failureModes: [
      "High Migration Time",
      "Low Resolution",
      "Missing Reference Peak",
      "Calibration Drift",
      "Abnormal Baseline",
      "Reference Marker Shift",
      "Reagent Performance Excursion",
    ],
    metrics: ["migration_time", "resolution", "noise", "temperature", "current_stability"],
    rationale: "Marker position, separation quality and baseline behaviour are emphasized for this fictional product family.",
  },
  "HelixIEF-400": {
    failureModes: [
      "Low Resolution",
      "Signal Noise Excursion",
      "Irregular Separation Profile",
      "Current Stability Excursion",
      "Temperature Excursion",
      "Calibration Drift",
      "Signal Height Below Threshold",
    ],
    metrics: ["migration_time", "resolution", "noise", "pressure_setpoint_error", "current_stability"],
    rationale: "Separation robustness and electrical stability are emphasized for this fictional product family.",
  },
  QuantSDS: {
    failureModes: [
      "High Migration Time",
      "Low Resolution",
      "Signal Height Below Threshold",
      "Abnormal Baseline",
      "Reagent Performance Excursion",
      "Component-Lot Correlation",
    ],
    metrics: ["migration_time", "resolution", "noise", "temperature"],
    rationale: "Migration, resolution and signal-integrity measures are emphasized for this fictional product family.",
  },
  "RapidSDS-X": {
    failureModes: [
      "High Migration Time",
      "Signal Noise Excursion",
      "Pressure Setpoint Drift",
      "Irregular Separation Profile",
      "Flow Restriction",
      "Calibration Drift",
      "Reagent Performance Excursion",
    ],
    metrics: ["migration_time", "resolution", "pressure_setpoint_error", "temperature", "current_stability"],
    rationale: "Migration-time behaviour, pressure control and reagent response are emphasized for this fictional product family.",
  },
  "NovaFlow-FX": {
    failureModes: [
      "Signal Noise Excursion",
      "Pressure Setpoint Drift",
      "Microbubble-Induced Signal Artifact",
      "Flow Restriction",
      "Temperature Excursion",
      "Abnormal Baseline",
      "Component-Lot Correlation",
    ],
    metrics: ["migration_time", "noise", "pressure_setpoint_error", "temperature"],
    rationale: "Fluid-path artefacts, pressure behaviour and signal stability are emphasized for this fictional product family.",
  },
});

const SUBMODES = {
  "High Migration Time": ["Above Upper Trend Limit", "Slow Ramp Profile"],
  "Low Resolution": ["Critical Pair Co-elution", "Broadened Peaks"],
  "Signal Noise Excursion": ["High-Frequency Noise", "Baseline Ripple"],
  "Pressure Setpoint Drift": ["Positive Drift", "Oscillating Drift"],
  "Missing Reference Peak": ["Reference Not Detected", "Reference Below Threshold"],
  "Microbubble-Induced Signal Artifact": ["Transient Spike", "Baseline Step"],
  "Irregular Separation Profile": ["Peak Tailing", "Split Peak"],
  "Flow Restriction": ["Partial Blockage", "Gradual Pressure Rise"],
  "Current Stability Excursion": ["Current Ripple", "Current Dropout"],
  "Temperature Excursion": ["High Temperature", "Low Temperature"],
  "Calibration Drift": ["Slope Drift", "Intercept Shift"],
  "Signal Height Below Threshold": ["Weak Main Peak", "Attenuated Signal"],
  "Abnormal Baseline": ["Baseline Rise", "Baseline Sag"],
  "Reference Marker Shift": ["Early Marker", "Late Marker"],
  "Reagent Performance Excursion": ["Lot Underperformance", "Age-Related Decline"],
  "Component-Lot Correlation": ["Lot-Linked Artifact", "Lot-Linked Shift"],
};

const MT_BASE = { "HelixIEF-200": 12.0, "HelixIEF-400": 14.5, QuantSDS: 9.0, "RapidSDS-X": 8.2, "NovaFlow-FX": 10.5 };
const RES_BASE = { "HelixIEF-200": 2.4, "HelixIEF-400": 2.6, QuantSDS: 2.1, "RapidSDS-X": 1.9, "NovaFlow-FX": 2.2 };
const RECOVERY = { "Signal Noise Excursion": 0.55, "Flow Restriction": 0.15, "Reference Marker Shift": 0.85 };

function perturb(rec, mode) {
  switch (mode) {
    case "High Migration Time":
      rec.migration_time += 2.4 + Math.abs(gauss()) * 0.5;
      break;
    case "Low Resolution":
      rec.resolution = Math.max(0.6, rec.resolution - 1.1);
      break;
    case "Signal Noise Excursion":
      rec.noise += 5 + Math.abs(gauss()) * 2;
      break;
    case "Pressure Setpoint Drift":
      rec.pressure_setpoint_error = 1.4 + Math.abs(gauss()) * 0.5;
      break;
    case "Missing Reference Peak":
      rec.signal_height *= 0.55;
      break;
    case "Microbubble-Induced Signal Artifact":
      rec.noise += 2.5;
      rec.signal_height *= 0.9;
      break;
    case "Irregular Separation Profile":
      rec.resolution -= 0.6;
      break;
    case "Flow Restriction":
      rec.pressure_setpoint_error = Math.abs(rec.pressure_setpoint_error) + 1.2;
      break;
    case "Current Stability Excursion":
      rec.current_stability -= 2.5;
      break;
    case "Temperature Excursion":
      rec.temperature += 1.8;
      break;
    case "Calibration Drift":
      rec.migration_time += 0.9;
      break;
    case "Signal Height Below Threshold":
      rec.signal_height *= 0.5;
      break;
    case "Abnormal Baseline":
      rec.noise += 1.8;
      break;
    case "Reference Marker Shift":
      rec.migration_time -= 1.1;
      break;
    case "Reagent Performance Excursion":
      rec.signal_height *= 0.8;
      rec.resolution -= 0.3;
      break;
    case "Component-Lot Correlation":
      rec.noise += 1.4;
      break;
    default:
      break;
  }
}

function buildRecords() {
  const records = [];
  let n = 0;
  for (let m = 0; m < 20; m++) {
    for (let k = 0; k < 210; k++) {
      n++;
      const dt = new Date(2025, m, 1 + Math.floor(rand() * 28), Math.floor(rand() * 24), Math.floor(rand() * 60));
      const product = weighted([
        ["HelixIEF-200", 22],
        ["HelixIEF-400", 22],
        ["QuantSDS", 18],
        ["RapidSDS-X", 20],
        ["NovaFlow-FX", 18],
      ]);
      const instrument = pick(INSTRUMENTS);
      const reagent = pick(REAGENT_LOTS);
      const component = pick(COMPONENT_LOTS);
      const intermittent = m % 5 === 1 || m % 5 === 2;
      const rec = {
        run_id: `RUN-${String(n).padStart(5, "0")}`,
        serial_number: `SN-${400000 + n}`,
        run_datetime: dt.toISOString(),
        month: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`,
        product,
        instrument,
        qc_stage: weighted([
          ["In-Process", 40],
          ["Release", 35],
          ["Stability", 15],
          ["Raw Material", 10],
        ]),
        attempt_number: 1,
        attempt_type: "Initial",
        reagent_lot: reagent,
        component_lot: component,
        migration_time: MT_BASE[product] + gauss() * 0.35,
        resolution: RES_BASE[product] + gauss() * 0.12,
        signal_height: 120 + gauss() * 15,
        noise: 4 + Math.abs(gauss()) * 1.2 + (instrument === "INS-04" ? 3.2 : 0),
        pressure_setpoint_error: gauss() * 0.4 + (instrument === "INS-07" && intermittent ? 0.9 : 0),
        temperature: 22 + gauss() * 0.5,
        current_stability: 99.2 + gauss() * 0.4,
      };
      if (product === "RapidSDS-X" && reagent === "RGT-107") rec.migration_time += 1.8;
      let failP = 0.115;
      if (product === "RapidSDS-X" && reagent === "RGT-107") failP = 0.17;
      if (product === "NovaFlow-FX" && component === "CMP-209") failP = 0.3;
      const failed = rand() < failP;
      if (failed) {
        let mode = null;
        const r = rand();
        if (instrument === "INS-04" && r < 0.62) mode = "Signal Noise Excursion";
        else if (instrument === "INS-07" && intermittent && rand() < 0.5) mode = "Pressure Setpoint Drift";
        else if (component === "CMP-209" && product === "NovaFlow-FX" && rand() < 0.75)
          mode = "Microbubble-Induced Signal Artifact";
        else if (product === "HelixIEF-200" && m >= 2 && m <= 4 && rand() < 0.55) mode = "Reference Marker Shift";
        else if (product === "RapidSDS-X" && reagent === "RGT-107" && rand() < 0.5) mode = "High Migration Time";
        else if (product === "RapidSDS-X" && rand() < 0.3) mode = "High Migration Time";
        else {
          const pool =
            product === "NovaFlow-FX" && component !== "CMP-209"
              ? FAILURE_MODES.map(([mode, w]) => [mode, mode === "Microbubble-Induced Signal Artifact" ? 1 : w])
              : FAILURE_MODES;
          mode = weighted(pool);
        }
        rec.result = "Fail";
        rec.failure_mode = mode;
        rec.failure_submode = pick(SUBMODES[mode]);
        perturb(rec, mode);
      } else {
        rec.result = "Pass";
        rec.failure_mode = "None";
        rec.failure_submode = "None";
      }
      if (failed) {
        const rp = RECOVERY[rec.failure_mode] ?? 0.4;
        const recovered = rand() < rp;
        rec.retest_result = recovered ? "Pass" : "Fail";
        rec.final_result = recovered ? "Pass" : "Fail";
        rec.synthetic_scrap_cost = recovered ? 0 : Math.round(400 + rand() * 1100);
        n++;
        const rt = {
          ...rec,
          run_id: `RUN-${String(n).padStart(5, "0")}`,
          attempt_number: 2,
          attempt_type: "Retest",
          run_datetime: new Date(dt.getTime() + 6 * 3600 * 1000).toISOString(),
          result: recovered ? "Pass" : "Fail",
          retest_result: "N/A",
          synthetic_scrap_cost: 0,
        };
        if (recovered) {
          rt.migration_time = MT_BASE[product] + gauss() * 0.3;
          rt.noise = 4 + Math.abs(gauss()) * 1.2;
          rt.resolution = RES_BASE[product] + gauss() * 0.1;
          rt.pressure_setpoint_error = gauss() * 0.4;
        }
        records.push(rec, rt);
      } else {
        rec.retest_result = "N/A";
        rec.final_result = "Pass";
        rec.synthetic_scrap_cost = 0;
        records.push(rec);
      }
    }
  }
  records.forEach((r) => {
    ["migration_time", "resolution", "signal_height", "noise", "pressure_setpoint_error", "temperature", "current_stability"].forEach(
      (k) => (r[k] = r2(r[k]))
    );
  });
  return records;
}

export const RECORDS = buildRecords();
export const MONTHS = [...new Set(RECORDS.map((r) => r.month))].sort();
export const DATA_PERIOD = { start: "Jan 2025", end: "Aug 2026" };

// ---------- analytics helpers ----------
export const ALL = "All";
export const DEFAULT_FILTERS = {
  from: MONTHS[0],
  to: MONTHS[MONTHS.length - 1],
  product: ALL,
  instrument: ALL,
  qcStage: ALL,
  attemptType: ALL,
  failureMode: ALL,
  reagentLot: ALL,
  componentLot: ALL,
  metric: ALL,
};

export function applyFilters(records, f) {
  const applicableModes = getApplicableFailureModes(f.product);
  return records.filter(
    (r) =>
      r.month >= f.from &&
      r.month <= f.to &&
      (f.product === ALL || r.product === f.product) &&
      (f.instrument === ALL || r.instrument === f.instrument) &&
      (f.qcStage === ALL || r.qc_stage === f.qcStage) &&
      (f.attemptType === ALL || r.attempt_type === f.attemptType) &&
      (f.failureMode === ALL || r.failure_mode === f.failureMode) &&
      (f.reagentLot === ALL || r.reagent_lot === f.reagentLot) &&
      (f.componentLot === ALL || r.component_lot === f.componentLot) &&
      (f.product === ALL || r.failure_mode === "None" || applicableModes.includes(r.failure_mode))
  );
}

const initialOf = (recs) => recs.filter((r) => r.attempt_type === "Initial");
const failedInitialOf = (recs) => recs.filter((r) => r.attempt_type === "Initial" && r.result === "Fail");

function topMode(recs) {
  const counts = {};
  failedInitialOf(recs).forEach((r) => (counts[r.failure_mode] = (counts[r.failure_mode] || 0) + 1));
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return entries.length ? { mode: entries[0][0], count: entries[0][1] } : { mode: "None", count: 0 };
}

export function computeInstrumentHealth(recs) {
  const initial = initialOf(recs);
  const fleetRate = initial.length ? failedInitialOf(recs).length / initial.length : 0;
  const maxDate = Math.max(...recs.map((r) => new Date(r.run_datetime).getTime()));
  const cutoff = maxDate - 30 * 24 * 3600 * 1000;
  return INSTRUMENTS.map((ins) => {
    const sub = initial.filter((r) => r.instrument === ins);
    const fails = sub.filter((r) => r.result === "Fail");
    const tm = topMode(sub);
    const share = fails.length ? tm.count / fails.length : 0;
    const failureRate = sub.length ? fails.length / sub.length : 0;
    const last30 = sub.filter((r) => new Date(r.run_datetime).getTime() >= cutoff);
    const last30Fpy = last30.length ? last30.filter((r) => r.result === "Pass").length / last30.length : null;
    return {
      instrument: ins,
      runs: sub.length,
      fpy: sub.length ? (sub.filter((r) => r.result === "Pass").length / sub.length) * 100 : 0,
      failureRate: failureRate * 100,
      topMode: tm.mode,
      topModeShare: share * 100,
      last30Fpy: last30Fpy === null ? null : last30Fpy * 100,
      flagged: fails.length >= 8 && share >= 0.45,
    };
  }).filter((h) => h.runs > 0);
}

export function computeKpis(recs) {
  const initial = initialOf(recs);
  const retests = recs.filter((r) => r.attempt_type === "Retest");
  const fpy = initial.length ? (initial.filter((r) => r.result === "Pass").length / initial.length) * 100 : 0;
  const finalYield = initial.length ? (initial.filter((r) => r.final_result === "Pass").length / initial.length) * 100 : 0;
  const recovery = retests.length ? (retests.filter((r) => r.result === "Pass").length / retests.length) * 100 : 0;
  const scrap = recs.reduce((s, r) => s + (r.synthetic_scrap_cost || 0), 0);
  const tm = topMode(recs);
  const flagged = computeInstrumentHealth(recs).filter((h) => h.flagged).length;
  return {
    fpy,
    finalYield,
    failureRate: 100 - fpy,
    retestRecovery: recovery,
    totalRuns: recs.length,
    scrap,
    topFailureMode: tm.mode,
    flaggedInstruments: flagged,
  };
}

export function monthlyTrend(recs) {
  const byMonth = {};
  initialOf(recs).forEach((r) => {
    byMonth[r.month] = byMonth[r.month] || { month: r.month, runs: 0, pass: 0, final: 0 };
    byMonth[r.month].runs++;
    if (r.result === "Pass") byMonth[r.month].pass++;
    if (r.final_result === "Pass") byMonth[r.month].final++;
  });
  return Object.values(byMonth)
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => ({ ...m, fpy: r2((m.pass / m.runs) * 100), finalYield: r2((m.final / m.runs) * 100) }));
}

export function paretoL1(recs) {
  const counts = {};
  failedInitialOf(recs).forEach((r) => (counts[r.failure_mode] = (counts[r.failure_mode] || 0) + 1));
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, e) => s + e[1], 0) || 1;
  let cum = 0;
  return entries.map(([mode, count]) => {
    cum += count;
    return { mode, count, cumulative: r2((cum / total) * 100) };
  });
}

export function paretoL2(recs) {
  const counts = {};
  failedInitialOf(recs).forEach((r) => (counts[r.failure_submode] = (counts[r.failure_submode] || 0) + 1));
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([submode, count]) => ({ submode, count }));
}

export function instrumentPerformance(recs) {
  const initial = initialOf(recs);
  return INSTRUMENTS.map((ins) => {
    const sub = initial.filter((r) => r.instrument === ins);
    if (!sub.length) return null;
    const tm = topMode(sub);
    return {
      instrument: ins,
      runs: sub.length,
      fpy: r2((sub.filter((r) => r.result === "Pass").length / sub.length) * 100),
      failureRate: r2((sub.filter((r) => r.result === "Fail").length / sub.length) * 100),
      topMode: tm.mode,
    };
  }).filter(Boolean);
}

export function lotPerformance(recs, key) {
  const initial = initialOf(recs);
  const groups = {};
  initial.forEach((r) => {
    const k = r[key];
    groups[k] = groups[k] || { lot: k, runs: 0, pass: 0 };
    groups[k].runs++;
    if (r.result === "Pass") groups[k].pass++;
  });
  return Object.values(groups)
    .sort((a, b) => a.lot.localeCompare(b.lot))
    .map((g) => ({ ...g, fpy: r2((g.pass / g.runs) * 100) }));
}

export function failureTrend(recs) {
  const top5 = paretoL1(recs)
    .slice(0, 5)
    .map((p) => p.mode);
  const byMonth = {};
  failedInitialOf(recs).forEach((r) => {
    if (!top5.includes(r.failure_mode)) return;
    byMonth[r.month] = byMonth[r.month] || { month: r.month };
    byMonth[r.month][r.failure_mode] = (byMonth[r.month][r.failure_mode] || 0) + 1;
  });
  return { modes: top5, data: Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month)) };
}

export function retestRecoveryByMode(recs) {
  const groups = {};
  recs
    .filter((r) => r.attempt_type === "Retest")
    .forEach((r) => {
      groups[r.failure_mode] = groups[r.failure_mode] || { mode: r.failure_mode, retests: 0, recovered: 0 };
      groups[r.failure_mode].retests++;
      if (r.result === "Pass") groups[r.failure_mode].recovered++;
    });
  return Object.values(groups)
    .filter((g) => g.retests >= 3)
    .sort((a, b) => b.retests - a.retests)
    .map((g) => ({ ...g, recovery: r2((g.recovered / g.retests) * 100) }));
}

export const METRICS = [
  { key: "migration_time", label: "Migration Time", unit: "min (synthetic)", min: 6, max: 18, bins: 24 },
  { key: "resolution", label: "Resolution", unit: "Rs (synthetic)", min: 0, max: 3.5, bins: 24 },
  { key: "noise", label: "Noise", unit: "mAU (synthetic)", min: 0, max: 16, bins: 24 },
  { key: "pressure_setpoint_error", label: "Pressure Setpoint Error", unit: "bar (synthetic)", min: -2.5, max: 2.5, bins: 24 },
  { key: "temperature", label: "Temperature", unit: "°C (synthetic)", min: 19, max: 26, bins: 24 },
  { key: "current_stability", label: "Current Stability", unit: "% (synthetic)", min: 94, max: 102, bins: 24 },
];

export function getProductApplicability(product) {
  if (product === ALL || !syntheticProductApplicability[product]) {
    return { failureModes: FAILURE_MODE_NAMES, metrics: METRICS.map((m) => m.key), rationale: "All synthetic product families and measures are in scope." };
  }
  return syntheticProductApplicability[product];
}

export function getApplicableFailureModes(product) {
  return getProductApplicability(product).failureModes;
}

export function getApplicableMetrics(product) {
  const allowed = getProductApplicability(product).metrics;
  return METRICS.filter((m) => allowed.includes(m.key));
}

// Keeps UI filter state inside the central fictional applicability contract.
export function sanitizeFiltersForProduct(filters) {
  const next = { ...filters };
  const modes = getApplicableFailureModes(next.product);
  const metricKeys = getApplicableMetrics(next.product).map((m) => m.key);
  if (next.failureMode !== ALL && !modes.includes(next.failureMode)) next.failureMode = ALL;
  if (next.metric !== ALL && !metricKeys.includes(next.metric)) next.metric = ALL;
  return next;
}

export function histogram(recs, cfg) {
  const bins = Array.from({ length: cfg.bins }, (_, i) => {
    const lo = cfg.min + (i * (cfg.max - cfg.min)) / cfg.bins;
    const hi = cfg.min + ((i + 1) * (cfg.max - cfg.min)) / cfg.bins;
    return { bin: r2((lo + hi) / 2), lo, hi, count: 0 };
  });
  recs.forEach((r) => {
    const v = r[cfg.key];
    if (v < cfg.min || v > cfg.max) return;
    const idx = Math.min(cfg.bins - 1, Math.floor(((v - cfg.min) / (cfg.max - cfg.min)) * cfg.bins));
    bins[idx].count++;
  });
  return bins;
}

export function quartiles(values) {
  const s = [...values].sort((a, b) => a - b);
  const q = (p) => s[Math.min(s.length - 1, Math.floor(p * s.length))];
  return { min: s[0], q1: q(0.25), median: q(0.5), q3: q(0.75), max: s[s.length - 1], n: s.length };
}

// ---------- Continuous Improvement helpers (RapidSDS-X program) ----------
export const CI_PRODUCT = "RapidSDS-X";
export const CI_SPEC = { lsl: 7.2, usl: 9.2, label: "Synthetic Demonstration Specification" };

export function ciRecords() {
  return RECORDS.filter((r) => r.product === CI_PRODUCT);
}

export function ciBaseline() {
  const recs = ciRecords();
  const initial = initialOf(recs);
  const retests = recs.filter((r) => r.attempt_type === "Retest");
  const fpy = (initial.filter((r) => r.result === "Pass").length / initial.length) * 100;
  const finalYield = (initial.filter((r) => r.final_result === "Pass").length / initial.length) * 100;
  const retestRate = (retests.length / initial.length) * 100;
  return { fpy: r2(fpy), finalYield: r2(finalYield), retestRate: r2(retestRate), scrapIndex: 100, runs: initial.length };
}

export function ciAfter() {
  const b = ciBaseline();
  return {
    fpy: r2(Math.min(97.2, b.fpy + 5.4)),
    finalYield: r2(Math.min(99, b.finalYield + 2.1)),
    retestRate: r2(b.retestRate * 0.53),
    scrapIndex: 55,
  };
}

export function ciPareto() {
  return paretoL1(ciRecords()).slice(0, 8);
}

export const CI_STRAT_DIMENSIONS = [
  { key: "instrument", label: "Instrument" },
  { key: "reagent_lot", label: "Reagent Lot" },
  { key: "component_lot", label: "Component Lot" },
  { key: "month", label: "Month" },
  { key: "qc_stage", label: "QC Stage" },
  { key: "attempt_type", label: "Initial vs Retest" },
];

export function ciStratify(dimKey) {
  const recs = ciRecords();
  const groups = {};
  recs.forEach((r) => {
    const k = r[dimKey];
    groups[k] = groups[k] || { name: k, runs: 0, fails: 0, hmt: 0 };
    groups[k].runs++;
    if (r.result === "Fail") {
      groups[k].fails++;
      if (r.failure_mode === "High Migration Time") groups[k].hmt++;
    }
  });
  return Object.values(groups)
    .map((g) => ({
      ...g,
      failureRate: r2((g.fails / g.runs) * 100),
      hmtShare: g.fails ? r2((g.hmt / g.fails) * 100) : 0,
    }))
    .sort((a, b) => (dimKey === "month" ? a.name.localeCompare(b.name) : b.failureRate - a.failureRate));
}

export function ciMigrationByReagent() {
  const initial = initialOf(ciRecords());
  const byLot = {};
  initial.forEach((r) => {
    byLot[r.reagent_lot] = byLot[r.reagent_lot] || [];
    byLot[r.reagent_lot].push(r.migration_time);
  });
  const stats = (vals) => {
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    const sd = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / (vals.length - 1));
    const cp = (CI_SPEC.usl - CI_SPEC.lsl) / (6 * sd);
    const cpk = Math.min(CI_SPEC.usl - mean, mean - CI_SPEC.lsl) / (3 * sd);
    return { mean: r2(mean), sd: r2(sd), cp: r2(cp), cpk: r2(cpk), n: vals.length, ...quartiles(vals) };
  };
  return Object.entries(byLot)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([lot, vals]) => ({ lot, ...stats(vals), outlier: stats(vals).mean > (CI_SPEC.usl + CI_SPEC.lsl) / 2 + 0.8 }));
}

export function ciInstrumentBox() {
  const initial = initialOf(ciRecords());
  return INSTRUMENTS.map((ins) => {
    const vals = initial.filter((r) => r.instrument === ins).map((r) => r.migration_time);
    if (vals.length < 5) return null;
    return { instrument: ins, ...quartiles(vals) };
  }).filter(Boolean);
}

export function ciEffectiveness() {
  const b = ciBaseline();
  const a = ciAfter();
  const rr = mulberry32(777);
  const phases = ["Baseline", "Implementation", "+30 Days", "+60 Days", "+90 Days"];
  const recurBefore = 9.6;
  const recurAfter = 2.1;
  return phases.map((phase, i) => {
    const p = i / (phases.length - 1);
    const j = () => (rr() - 0.5) * 0.8;
    return {
      phase,
      fpy: r2(b.fpy + (a.fpy - b.fpy) * p + (i === 0 ? 0 : j())),
      retestRate: r2(b.retestRate + (a.retestRate - b.retestRate) * p + (i === 0 ? 0 : j() * 0.5)),
      scrapIndex: Math.round(b.scrapIndex + (a.scrapIndex - b.scrapIndex) * p + (i === 0 ? 0 : j())),
      recurrence: r2(recurBefore + (recurAfter - recurBefore) * p + (i === 0 ? 0 : j() * 0.4)),
    };
  });
}
